// A BLE lamp's last known state, as stored in BleNode.LastValues — pure, tested
// in lampState.test.js. Shared by both lamp families:
//
//   "<a>,<b>,<p>"      a, b  0..255   LampSmart: white, warm
//                                     Mesh:      brightness, coolness (meshValues.js)
//                      p     1 = ON, 0 = OFF
//
// ⚠️ ON/OFF IS ITS OWN FIELD, NOT "a and b are zero". Seen 2026-10-11: every lamp
// on the Individual page read "0% · white" because Off wrote "0,…" (losing the
// brightness) and On wrote nothing at all, so a lamp switched on still showed
// off. Off now keeps the values and sets p = 0; On keeps them and sets p = 1,
// which is also what the lamp itself does (it comes back at its last level).
//
// Rows written before this have only "a,b": p is then inferred from the values
// (mesh: brightness > 0; LampSmart: either channel > 0).

import { parseServerTime } from '../devices/firmwareInfo';

const clamp = (v) => Math.max(0, Math.min(255, Math.round(Number(v) || 0)));
const darkValues = (a, b, mesh) => (mesh ? a === 0 : a === 0 && b === 0);

/** -> { a, b, on } or null when nothing usable was ever recorded. */
export function parseLampState(raw, { mesh = false } = {}) {
  const parts = String(raw ?? '').split(',').map((x) => parseInt(x.trim(), 10));
  if (parts.length < 2 || Number.isNaN(parts[0]) || Number.isNaN(parts[1])) return null;
  const a = clamp(parts[0]);
  const b = clamp(parts[1]);
  const p = parts.length >= 3 && (parts[2] === 0 || parts[2] === 1) ? parts[2] : null;
  return { a, b, on: p === null ? !darkValues(a, b, mesh) : p === 1 };
}

export const formatLampState = (a, b, on) => `${clamp(a)},${clamp(b)},${on ? 1 : 0}`;

/** After an ON: same values, on. Dark or unknown values -> full, since the lamp shows SOMETHING. */
export function stateAfterOn(raw, { mesh = false } = {}) {
  const s = parseLampState(raw, { mesh });
  if (!s) return mesh ? formatLampState(255, 128, true) : formatLampState(255, 255, true);
  if (darkValues(s.a, s.b, mesh)) return mesh ? formatLampState(255, s.b, true) : formatLampState(255, 255, true);
  return formatLampState(s.a, s.b, true);
}

/** After an OFF: the values are KEPT (the next On restores them), only p changes. */
export function stateAfterOff(raw, { mesh = false } = {}) {
  const s = parseLampState(raw, { mesh });
  if (!s) return mesh ? formatLampState(0, 128, false) : formatLampState(0, 0, false);
  return formatLampState(s.a, s.b, false);
}

/**
 * What the SLIDERS show. The stored values outlive an Off (so On restores them),
 * but a slider showing full brightness on a lamp that is off is a lie — every
 * button (On, Off, Night, a preset) must move the sliders. Off: brightness 0
 * (mesh keeps its colour); LampSmart: both channels 0.
 */
export function sliderValues(raw, { mesh = false } = {}) {
  const s = parseLampState(raw, { mesh });
  if (!s) return mesh ? [0, 128] : [0, 0];
  if (!s.on) return mesh ? [0, s.b] : [0, 0];
  return [s.a, s.b];
}

/** After setting values directly: on unless they are dark. */
export const stateForValues = (a, b, { mesh = false } = {}) => formatLampState(a, b, !darkValues(clamp(a), clamp(b), mesh));

/**
 * ⚠️ REVERSED WIRING (BleNode.SwapWarm). Some lamps leave the factory with warm
 * and cool LEDs swapped — only some of a batch. LastValues and the sliders
 * always hold the colour the USER sees; only what goes on the air is mirrored
 * (LampSmart: the two channels swapped; mesh: coolness -> 255 - coolness), and
 * what a mesh lamp reports is mirrored back the same way.
 */
export const isSwapped = (lamp) => Number(lamp?.SwapWarm) === 1;
export const mirrorCoolness = (coolness, swapped) => (swapped ? 255 - clamp(coolness) : clamp(coolness));

/**
 * When a lamp (or node) was last OPERATED: the newer of the server's
 * LastUsedAt (time-zone corrected like every server time — parseServerTime) and
 * this phone's own LastUsedLocalMs, stamped the moment a command is sent so
 * the row does not wait for the next server read. -> epoch ms or null.
 */
export function lastUsedMs(row) {
  const server = parseServerTime(row?.LastUsedAt);
  const local = Number(row?.LastUsedLocalMs) || null;
  if (server == null) return local;
  if (local == null) return server;
  return Math.max(server, local);
}

/** "just now" · "40 s ago" · "3 min ago" · "2 h ago" · "3 d ago" — with who, if known. */
export function lastUsedText(row, now = Date.now()) {
  const t = lastUsedMs(row);
  if (t == null) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  let ago;
  if (s < 10) ago = 'just now';
  else if (s < 60) ago = `${s} s ago`;
  else if (s < 3600) ago = `${Math.floor(s / 60)} min ago`;
  else if (s < 48 * 3600) ago = `${Math.floor(s / 3600)} h ago`;
  else ago = `${Math.floor(s / 86400)} d ago`;
  // This phone's own press is "you", until the server's row (with a name) arrives.
  const localNewer = Number(row?.LastUsedLocalMs) && t === Number(row.LastUsedLocalMs);
  const by = localNewer ? 'you' : row?.LastUsedBy;
  return by ? `${ago} · ${by}` : ago;
}

/** "3 of 5 on" for a group header; lamps never recorded count as unknown. */
export function countOn(lamps, { mesh = false } = {}) {
  let on = 0;
  let known = 0;
  for (const l of lamps || []) {
    const s = parseLampState(l?.LastValues, { mesh });
    if (s) { known += 1; if (s.on) on += 1; }
  }
  return { on, known, total: (lamps || []).length };
}
